import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from './AuthContext';
import { API_BASE } from '../config/api';

const InvitationContext = createContext();

/**
 * InvitationContext
 *
 * Surfaces *all* pending invitations addressed to the logged-in user:
 *   - film invitations  (existing flow at /api/invitations)
 *   - workspace invitations (new flow at /api/workspaces/my-invitations)
 *
 * The `invitations` array preserves the film-invitation shape used elsewhere
 * (header badge, page count) so we don't regress callers; workspace invites
 * are exposed separately as `workspaceInvitations`.
 *
 * `invitationCount` is the union (film + workspace) so the nav badge reflects
 * everything that needs the user's attention.
 */
export const InvitationProvider = ({ children }) => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);          // film invites
  const [workspaceInvitations, setWorkspaceInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userId = user?.id;

  const fetchInvitations = useCallback(async () => {
    if (!userId) {
      setInvitations([]);
      setWorkspaceInvitations([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const headers = getAuthHeaders();
      const [filmRes, wsRes] = await Promise.allSettled([
        axios.get(`${API_BASE}/invitations`, { headers }),
        axios.get(`${API_BASE}/workspaces/my-invitations`, { headers }),
      ]);

      if (filmRes.status === 'fulfilled') {
        setInvitations(filmRes.value.data || []);
      } else {
        console.warn('Failed to fetch film invitations:', filmRes.reason);
        setInvitations([]);
      }

      if (wsRes.status === 'fulfilled') {
        setWorkspaceInvitations(wsRes.value.data || []);
      } else {
        // 503 means the migration hasn't been applied; log but don't surface to user
        console.warn('Failed to fetch workspace invitations:', wsRes.reason);
        setWorkspaceInvitations([]);
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const value = useMemo(() => ({
    invitations,
    workspaceInvitations,
    invitationCount: invitations.length + workspaceInvitations.length,
    filmInvitationCount: invitations.length,
    workspaceInvitationCount: workspaceInvitations.length,
    loading,
    error,
    refreshInvitations: fetchInvitations
  }), [invitations, workspaceInvitations, loading, error, fetchInvitations]);

  return (
    <InvitationContext.Provider value={value}>
      {children}
    </InvitationContext.Provider>
  );
};

export const useInvitations = () => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error('useInvitations must be used within an InvitationProvider');
  }
  return context;
};

export default InvitationContext;
